import { PagedResults, ServiceResponse } from "@se/platform";
import { SearchResultPage } from "../models/SearchResultPage";
import { IMessagingService } from "../services";
import { SearchForm } from "./searchForm";
import { ILoggingService } from "../services/interfaces/loggingService";

class SearchAutoComplete extends SearchForm {
    private resultsOutput: HTMLElement | null;
    private resultTemplate: HTMLTemplateElement;
    private results: SearchResultPage[];
    private resultsOutputItems: HTMLElement[];
    private noResultsOutput: HTMLElement;
    private autoCompleteResultsAnnouncement: HTMLElement;
    private autocompleteId: string;
    private autocompletePagesize: string;
    private autocompletePageNumber: string;

    private onSearchFormInputFocus: EventListener;
    private onSearchFormInputChange: EventListener;
    private onSearchFormInputKeyDown: EventListener;
    private onResultsOutputKeyDown: EventListener;
    private onResultsOutputClick: EventListener;
    private onResultsSubmitBtnFocus: EventListener;
    
    constructor(componentEl: HTMLFormElement, messagingService: IMessagingService, private appInsights: ILoggingService) {
        super(componentEl, messagingService);

        this.resultsOutput = this.searchForm.querySelector(".js-search-form-results");
        this.searchFormInput = this.searchForm.querySelector(".js-search-form-input");
        this.autoCompleteResultsAnnouncement = this.searchForm.querySelector(".js-search-results-announcement");

        if (!this.resultsOutput) {
            return; // no autocomplete found
        }

        // get options via data attributes
        ({
            autocompleteId: this.autocompleteId,
            autocompletePagesize: this.autocompletePagesize = "5",
            autocompletePageNumber: this.autocompletePageNumber = "1",
        } = this.searchForm.dataset);

        this.resultTemplate = this.searchForm.querySelector("[data-template='results-item']");
        this.noResultsOutput = this.resultsOutput.querySelector(".js-search-form-no-results");

        this.results = [];
        this.resultsOutputItems = [];

        this.onSearchFormInputFocus = this.onSearchFormInputFocusHandler.bind(this);
        this.onSearchFormInputChange = this.onSearchFormInputChangeHandler.bind(this);
        this.onSearchFormInputKeyDown = this.onSearchFormInputKeyDownHandler.bind(this);
        this.onResultsOutputKeyDown = this.onResultsOutputKeyDownHandler.bind(this);
        this.onResultsOutputClick = this.onResultsOutputClickHandler.bind(this);
        this.onResultsSubmitBtnFocus = this.onResultsSubmitBtnFocusHandler.bind(this);

        this.initAutocomplete();
    }

    protected async initAutocomplete() {
        // Bind Event Handlers
        this.searchFormInput.addEventListener("focus", this.onSearchFormInputFocus);
        this.searchFormInput.addEventListener("input", this.debounceInput(this.onSearchFormInputChange, 250));
        this.searchFormInput.addEventListener("keydown", this.onSearchFormInputKeyDown);
        if (this.submitBtn) {
            this.submitBtn.addEventListener("focus", this.onResultsSubmitBtnFocus);
        }
        this.resultsOutput.addEventListener("keydown", this.onResultsOutputKeyDown);
        this.resultsOutput.addEventListener("click", this.onResultsOutputClick);

        document.addEventListener("click", (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (!target.closest(".js-search-form-results") && !target.closest(".js-search-form-input")) {
                this.hideResults();
            }
        });
    }

    private debounceInput(callback, delay) {
        let timeout;
        return (...args) => {
            const context = this;
            clearTimeout(timeout);
            timeout = setTimeout(() => callback.apply(context, args), delay);
        };
    }

    // Event Handlers
    private onSearchFormInputFocusHandler() {
        if (this.hasResults()) {
            this.showResults();
        }
    }

    private onSearchFormInputChangeHandler(e: InputEvent) {
        const target = e.target as HTMLInputElement;
        let searchterm = target.value.trim();

        if (searchterm.length > 2) {

            // if there is at least 2 chars, then encode the search term before request.
            searchterm = encodeURIComponent(searchterm);

            this.fetchResults(searchterm)
                .then((response) => {
                    const { results } = response.content;
                    if (Array.isArray(results) && results.length > 0) {
                        this.results = results;
                        this.outputResults(this.buildResults(this.results));
                        this.showResults();
                    } else {
                        this.showNoResults();
                    }
                })
                .catch((error) => {
                    this.appInsights.LogMessage('searchAutoComplete:onSearchFormInputChangeHandler error: ' + error);
                });
        } else {
            this.resetResults();
        }
    }

    private onSearchFormInputKeyDownHandler(e: KeyboardEvent) {
        const items = this.resultsOutputItems;
        if (e.key === "ArrowDown" && this.hasResults() && items.length > 0) {
            this.focusResult(items[0]);
            e.preventDefault();
        } else if (e.key === "Escape" || (e.key === "Tab" && e.shiftKey)) {
            this.hideResults();
        }
    }

    private onResultsOutputKeyDownHandler(e: KeyboardEvent) {
        const target = e.target as HTMLElement;
        const items = this.resultsOutputItems;

        switch (e.key) {
            case "Enter":
                e.target.dispatchEvent(new Event("click", { bubbles: true }));
                break;
            case "ArrowDown":
                if (target !== items[items.length - 1]) {
                    this.focusResult(items[items.indexOf(target) + 1]);
                }
                e.preventDefault();
                break;
            case "ArrowUp":
                items.forEach((item) => item.setAttribute("aria-selected", "false"));
                if (target === items[0]) {
                    this.searchFormInput.focus();
                } else {
                    this.focusResult(items[items.indexOf(target) - 1]);
                    e.preventDefault();
                }
                break;
            case "Escape":
                this.hideResults();
                this.searchFormInput.focus();
                break;
            case "Tab":
                if (!e.shiftKey) {
                    this.hideResults();
                }
                break;
        }
    }

    private onResultsOutputClickHandler(e: MouseEvent) {
        const target = e.target as HTMLElement;
        const resultItem = target.closest("[role='option']") as HTMLElement;
        this.searchFormInput.value = resultItem.querySelector("[data-result-title]").innerHTML;
        this.hideResults();
        if (resultItem.hasAttribute("data-result-url")) {
            window.location.href = resultItem.dataset.resultUrl;
        }
    }

    private onResultsSubmitBtnFocusHandler() {
        this.hideResults();
    }

    // Methods
    private hasResults() {
        return this.results.length > 0;
    }

    private async fetchResults(searchterm: string): Promise<ServiceResponse<PagedResults<SearchResultPage>>> {
        this.resetResults();
        const url = `/api/search/autocomplete?searchResultsId=${this.autocompleteId}&searchTerm=${searchterm}&resultsPerPage=${this.autocompletePagesize}`;
        const response = await fetch(url, {
            method: "GET",
            headers: {
                "Content-Type": "application/json; charset=utf-8",
            },
        });
        if (response.ok) {
            var responseObj = await response.json();
            return responseObj;
        } else {
            const error = new Error("Autocomplete fetch failed");
            return Promise.reject(error);
        }
    }

    private buildResults(results: SearchResultPage[]) {
        if (!this.resultTemplate) {
            return;
        }
        return results.map((result, i) => {
            const clone = this.resultTemplate.content.cloneNode(true) as HTMLElement;

            const container = clone.querySelector("[data-result-container]");

            container.setAttribute("aria-posinset", `${i + 1}`);
            container.setAttribute("aria-setsize", `${results.length}`);
            result.url ? container.setAttribute("data-result-url", result.url) : null;

            const title = clone.querySelector("[data-result-title]") as HTMLSpanElement;
            const category = clone.querySelector("[data-result-category]") as HTMLSpanElement;

            result.title ? (title.innerText = result.title) : title.remove();
            result.category ? (category.innerText = `in ${result.category}`) : category.remove();
            return clone;
        });
    }

    private outputResults(elements) {
        elements.forEach((node) => {
            this.resultsOutput.appendChild(node);
        });
    }

    private showResults() {
        this.resultsOutputItems = [...this.resultsOutput.querySelectorAll("[role='option']")] as HTMLElement[];

        this.autoCompleteResultsAnnouncement.textContent = this.resultsOutputItems.length === 1 ? "There is 1 option." : `There are ${this.resultsOutputItems.length} options.`;
        
        this.searchFormInput.setAttribute("aria-expanded", "true");
    }

    private showNoResults() {
        this.autoCompleteResultsAnnouncement.textContent = 'There are zero options.';

        if (this.noResultsOutput) {
            this.resultsOutput.appendChild(this.noResultsOutput);
            this.searchFormInput.setAttribute("aria-expanded", "true");
        }
    }

    private hideResults() {
        if (this.resultsOutputItems) {
            this.resultsOutputItems.forEach((item) => item.setAttribute("aria-selected", "false"));
        }
        this.searchFormInput.setAttribute("aria-expanded", "false");
    }

    private resetResults() {
        while (this.resultsOutput.firstChild) {
            this.resultsOutput.removeChild(this.resultsOutput.firstChild);
        }
        this.results = [];
        this.resultsOutputItems = null;
        this.hideResults();
    }

    private focusResult(target) {
        this.resultsOutputItems.forEach((item) => item.setAttribute("aria-selected", "false"));
        target.setAttribute("aria-selected", "true");
        target.focus();
    }
}

export { SearchAutoComplete };
